<template>
  <div class="finance_box" ref="myElementRef">
    <el-alert
      v-if="usingDefault"
      type="warning"
      :closable="false"
      show-icon
      class="tip-alert"
      :title="t('attendance.defaultRulesWarning')"
    />
    <el-alert type="info" :closable="false" show-icon class="tip-alert">
      <template #title>
        <span>{{ t('attendance.ruleInfoTitle') }}</span>
      </template>
    </el-alert>

    <div class="toolbar">
      <el-button type="primary" icon="Plus" @click="addRow">{{ t('attendance.addRule') }}</el-button>
      <el-button @click="resetDefault">{{ t('attendance.restoreDefault') }}</el-button>
      <el-button type="success" :loading="saving" @click="save">{{ t('attendance.saveConfig') }}</el-button>
    </div>

    <div class="table_box">
      <el-table :data="tableData" border stripe :header-cell-style="{ background: '#ECF5FF' }" :height="state.offsetHeight" table-layout="auto">
        <el-table-column type="index" :label="t('table.index')" width="70" />
        <el-table-column :label="t('attendance.serviceYears')" min-width="180">
          <template #default="{ row }">
            <template v-if="row.is_catchall">
              <span>{{ t('attendance.fullYears') }} </span>
              <el-input-number v-model="row.catchall_from" :min="1" :max="99" controls-position="right" class="order-input" />
              <span> {{ t('attendance.yearsAndAbove') }}</span>
            </template>
            <template v-else>
              <span>{{ t('attendance.fullYears') }} </span>
              <el-input-number v-model="row.anniversary_order" :min="1" :max="99" controls-position="right" class="order-input" />
              <span> {{ t('attendance.yearsAnniversary') }}</span>
            </template>
          </template>
        </el-table-column>
        <el-table-column :label="t('attendance.annualDaysInCycle')" width="180" align="center">
          <template #default="{ row }">
            <el-input-number v-model="row.days" :min="0" :max="365" controls-position="right" />
          </template>
        </el-table-column>
        <el-table-column :label="t('attendance.description')" min-width="280">
          <template #default="{ row }">
            <el-input v-model="row.rule_text" :placeholder="t('attendance.ruleTextPlaceholder')" />
          </template>
        </el-table-column>
        <el-table-column :label="t('table.operation')" width="100" fixed="right" align="center">
          <template #default="{ $index }">
            <el-button type="danger" link :disabled="tableData.length <= 1" @click="removeRow($index)">{{ t('common.delete') }}</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted, onUnmounted, nextTick } from 'vue'
import { annualDayRuleApi, annualDayRuleSaveApi } from '@/api/attendance/annualLeaveRule'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useAttendancePageI18n, t } from '@/composables/useAttendancePageI18n.js'

const myElementRef = ref()
const tableData = ref([])
const usingDefault = ref(false)
const saving = ref(false)
const state = reactive({ offsetHeight: 400 })

const DEFAULT_RULES = [
  { anniversary_order: 1, days: 7, rule_text: '入职满1周年当日起本周期（12 个月内）' },
  { anniversary_order: 2, days: 7, rule_text: '入职满2周年当日起本周期（12 个月内）' },
  { anniversary_order: 3, days: 8, rule_text: '入职满3周年当日起本周期（12 个月内）' },
  { anniversary_order: 4, days: 9, rule_text: '入职满4周年当日起本周期（12 个月内）' },
  { anniversary_order: 5, days: 10, rule_text: '入职满5周年当日起本周期（12 个月内）' },
  { anniversary_order: 6, days: 11, rule_text: '入职满6周年当日起本周期（12 个月内）' },
  { anniversary_order: 7, days: 12, rule_text: '入职满7周年当日起本周期（12 个月内）' },
  { anniversary_order: 8, days: 13, rule_text: '入职满8周年当日起本周期（12 个月内）' },
  { catchall_from: 9, days: 14, rule_text: '入职满9周年及以后各周年起本周期（12 个月内）', is_catchall: true }
]

const normalizeRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return DEFAULT_RULES.map((item) => ({ ...item }))
  }
  return rows.map((row, index) => {
    const isCatchall = row.anniversary_order == null || row.anniversary_order === ''
    return {
      anniversary_order: isCatchall ? null : Number(row.anniversary_order),
      catchall_from: isCatchall ? Number(row.catchall_from ?? row.anniversary_order ?? 9) : null,
      days: Number(row.days ?? 0),
      rule_text: row.rule_text || '',
      is_catchall: isCatchall,
      sort_order: row.sort_order ?? index + 1
    }
  })
}

const sortRows = () => {
  tableData.value.sort((a, b) => getOrderValue(a) - getOrderValue(b))
  const maxOrder = Math.max(...tableData.value.map(getOrderValue))
  tableData.value.forEach((row) => {
    row.is_catchall = getOrderValue(row) === maxOrder
    if (row.is_catchall) {
      row.catchall_from = getOrderValue(row)
      row.anniversary_order = null
    } else {
      row.anniversary_order = getOrderValue(row)
      row.catchall_from = null
    }
  })
}

const getOrderValue = (row) => {
  if (row.is_catchall || row.anniversary_order == null || row.anniversary_order === '') {
    return Number(row.catchall_from || 1)
  }
  return Number(row.anniversary_order)
}

const toPayload = () => {
  sortRows()
  return tableData.value.map((row, index) => ({
    anniversary_order: row.is_catchall ? null : row.anniversary_order,
    catchall_from: row.is_catchall ? row.catchall_from : null,
    days: row.days,
    rule_text: row.rule_text,
    sort_order: index + 1
  }))
}

const offHeight = () => {
  if (myElementRef.value) state.offsetHeight = myElementRef.value.offsetHeight - 180
}

const applyLabels = () => {};

useAttendancePageI18n(applyLabels);

const load = () => {
  annualDayRuleApi()
    .then((res) => {
      if (res?.code !== 200) {
        ElMessage.error(res?.msg || t('attendance.loadFailed'))
        return
      }
      usingDefault.value = !!res.data?.using_default
      tableData.value = normalizeRows(res.data?.rules)
      sortRows()
    })
    .catch(() => ElMessage.error(t('attendance.loadFailed')))
}

const addRow = () => {
  const max = tableData.value.length ? Math.max(...tableData.value.map(getOrderValue)) : 0
  tableData.value.push({
    anniversary_order: max + 1,
    catchall_from: null,
    days: 7,
    rule_text: '',
    is_catchall: false
  })
  sortRows()
}

const removeRow = (index) => {
  tableData.value.splice(index, 1)
  sortRows()
}

const resetDefault = () => {
  ElMessageBox.confirm(t('attendance.confirmRestoreDefault'), t('attendance.restoreDefaultTitle'), { type: 'warning' })
    .then(() => {
      tableData.value = DEFAULT_RULES.map((item) => ({ ...item }))
      sortRows()
    })
    .catch(() => {})
}

const rowInvalidMsg = (rowNum, msg) => {
  return t('attendance.rowInvalidPrefix', { row: rowNum }) + msg
}

const save = () => {
  if (!tableData.value.length) {
    ElMessage.warning(t('attendance.keepOneRule'))
    return
  }
  for (let i = 0; i < tableData.value.length; i++) {
    const row = tableData.value[i]
    const order = getOrderValue(row)
    if (!order || order < 1) {
      ElMessage.warning(rowInvalidMsg(i + 1, t('attendance.invalidAnniversary')))
      return
    }
    if (row.days == null || row.days < 0) {
      ElMessage.warning(rowInvalidMsg(i + 1, t('attendance.invalidAnnualDays')))
      return
    }
  }
  saving.value = true
  annualDayRuleSaveApi(JSON.stringify({ rules: toPayload() }))
    .then((res) => {
      if (res?.code === 200) {
        ElMessage.success(t('attendance.saveSuccess'))
        usingDefault.value = false
        if (res.data?.rules) tableData.value = normalizeRows(res.data.rules)
        sortRows()
      } else {
        ElMessage.error(res?.msg || t('attendance.saveFailed'))
      }
    })
    .catch(() => ElMessage.error(t('attendance.saveFailed')))
    .finally(() => {
      saving.value = false
    })
}

onMounted(() => {
  applyLabels();
  load()
  nextTick(() => offHeight())
  window.onresize = () => nextTick(() => offHeight())
})
onUnmounted(() => {
  window.onresize = null
})
</script>

<style lang="less" scoped>
.finance_box {
  flex: 1;
  overflow: auto;
  background-color: #fff;
  border-radius: 6px;
  padding: 10px;
  margin-right: 10px;
  min-height: 520px;
}
.tip-alert {
  margin-bottom: 12px;
  line-height: 1.6;
}
.toolbar {
  margin-bottom: 12px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.table_box {
  width: 100%;
}
.order-input {
  width: 110px;
  margin: 0 4px;
}
</style>
